/* eslint-disable no-console */
import React, { useEffect, useState } from 'react';
import { Modal, Box, Typography, Grid2, TextField, Button, IconButton } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import styles from './Modals.module.css';
import {
  createDrug,
  deleteMed,
  Drug,
  DrugPostRequest,
  DrugSchema,
  getDrug,
  getDrugDetails,
} from './helper.stim';
import {
  convertToLocalDateTime,
  convertToUTCFormat,
  convertUTCToLocalTime,
  formatDateToLocalYYYYMMDD,
} from '../../services/time';
import { useAlert } from '../../components/alertProvider';
import { AxiosError } from 'axios';
import {ReactComponent as WarningRedCircle} from '../../assets/SVG/Warning-red.svg';
import {ReactComponent as TrashIcon} from '../../assets/SVG/trash.svg';
import {ReactComponent as CloseModalIcon} from '../../assets/SVG/close-circle-greay.svg';
import { useVerboseLog } from '../../utils/debug';

interface AddDrugModalProps {
  drugModalOpen: boolean;
  handleDrugModalClose: () => void;
  stim: number;
  date: Date;
  id?: string;
  type?: string;
  medId?: number;
}

const AddDrugModal: React.FC<AddDrugModalProps> = ({
  drugModalOpen,
  handleDrugModalClose,
  stim,
  date,
  id,
  type,
  medId,
}) => {
  const { addAlert } = useAlert();
  const verboseLog = useVerboseLog();

  const [med, setMed] = useState<Drug | null>(null);
  const [selectedDrug, setSelectedDrug] = useState<DrugSchema | null>(null);
  const [deleteMode, setDeleteMode] = useState<boolean>(false);

  // Fetch drug details by medId
  useEffect(() => {
    if (medId) {
      getDrugDetails(medId)
        .then((drug: DrugSchema) => {
          console.log('Fetched selectedDrug:', drug);
          setSelectedDrug(drug);
        })
        .catch((error) => console.error('Error fetching drug details:', error));
    }
  }, [medId]);

  // Fetch additional drug info by id
  useEffect(() => {
    if (id) {
      getDrug(id)
        .then((drug: Drug) => {
          console.log('Fetched med:', drug);
          setMed(drug);
        })
        .catch((error) => console.error('Error fetching drug:', error));
    }
  }, [id]);

  // Validation schema
  const validationSchema = Yup.object({
    dosage: Yup.string().required('Dosage is required'),
    time: Yup.string().required('Time is required'),
    days: Yup.number()
      .required('Number of days is required')
      .positive('Days must be a positive number')
      .integer('Days must be an integer'),
  });

  // Compute the number of days dynamically
  const calculateDays = (): string => {
    if (selectedDrug?.start_date && selectedDrug?.end_date) {
      const start = convertToLocalDateTime(selectedDrug.start_date);
      const end = convertToLocalDateTime(selectedDrug.end_date);
      return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24) + 1).toString();
    }
    return '';
  };

  const handleDelete = () => {
    if (selectedDrug?.id && deleteMode) {
      deleteMed(selectedDrug?.id)
        .then(() => {
          addAlert('Deleted Medication successfully', 'success');
        })
        .catch((error)=>{
          verboseLog('Error deleting drug:', error.response.data.errors[0].detail);
          addAlert(error.response.data.errors[0].detail, 'error');
        })
        .finally(() =>  {
          setDeleteMode(false);
          handleDrugModalClose();
        });
    }
  };

  // Initialize Formik
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      dosage: selectedDrug?.quantity?.toString() ?? '',
      days: calculateDays(),
      time: selectedDrug?.start_time ?? '',
    },
    validationSchema,
    onSubmit: (values) => {
      if (id) {
        const medType = type === 'trigger' ? 'TRIGGER' : 'STIMULATION';
        const body: DrugPostRequest = {
          drug: parseInt(id),
          drug_type: medType,
          start_date: selectedDrug?.start_date? formatDateToLocalYYYYMMDD(new Date(selectedDrug?.start_date)): formatDateToLocalYYYYMMDD(date),
          quantity: parseInt(values.dosage),
          start_time: convertToUTCFormat(values.time),
          number_of_days: parseInt(values.days),
          stim_chart: stim,
        };

        console.log('Submitting body:', body);

        createDrug(body, medId)
          .then(() => {
            if(medId){
              addAlert('Dose Updated successfully', 'success');  
            }else{
              addAlert('Dose Created successfully', 'success');  
            }
          })
          .catch((error: AxiosError) => {
            addAlert(error.cause?.message?.toString() || 'Failed to create dose', 'error');
          })
          .finally(() => {
            handleDrugModalClose();
          });
      
      
      }
      // Close modal after submission
    },
  });

  // Update formik values whenever selectedDrug changes
  useEffect(() => {
    if (selectedDrug) {
      console.log('Updating formik values with selectedDrug:', selectedDrug);
      formik.setValues({
        dosage: selectedDrug.quantity?.toString() ?? '',
        days: calculateDays(),
        time: selectedDrug.start_time ?? '',
      });
    }
    /* eslint-disable-next-line */
  }, [selectedDrug]);

  return (
    <Modal open={drugModalOpen} onClose={handleDrugModalClose}>
      <Box className={styles.modal} p={2}>
        <Box className={styles.modalHeader}>
          <Typography variant="h3" gutterBottom>
            {type?.toLocaleUpperCase() === 'TRIGGER' ? 'Add Trigger Doses':' Add Stim Doses'}
          </Typography>
          <Box>
            {selectedDrug && !deleteMode &&
            <IconButton 
              disableRipple
              className={styles.modalCancelBtn}
              onClick={() => setDeleteMode(true)}
              disabled={deleteMode} 
            >
              <TrashIcon style={{height: '24px'}} />
            </IconButton>
            }
            <IconButton 
              disableRipple
              className={styles.modalCancelBtn}              
              onClick={()=>{handleDrugModalClose(); setDeleteMode(false);}}
            >
              <CloseModalIcon style={{height: '24px'}} />
            </IconButton>
          </Box>
        </Box>
        <Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              rowGap: '1rem',
              columnGap: '1.5rem',
              flexWrap: 'wrap',
              padding: '1rem',
            }}
          >
            <Box className={styles.info}>
              <Typography component={'label'}>Start Date</Typography>
              <Typography component={'span'}>
                {selectedDrug?.start_date
                  ? formatDateToLocalYYYYMMDD(new Date(selectedDrug.start_date))
                  : formatDateToLocalYYYYMMDD(date)}
              </Typography>
            </Box>
            {med?.name && (
              <Box className={styles.info}>
                <Typography component={'label'}>Name</Typography>
                <Typography component={'span'}>{med.name}</Typography>
              </Box>
            )}
            {med?.brand_name && (
              <Box className={styles.info}>
                <Typography component={'label'}>Brand</Typography>
                <Typography component={'span'}>{med.brand_name}</Typography>
              </Box>
            )}
            {med?.strength && (
              <Box className={styles.info}>
                <Typography component={'label'}>Strength</Typography>
                <Typography component={'span'}>{med.strength}</Typography>
              </Box>
            )}
          </Box>
          <form onSubmit={formik.handleSubmit}>
            <Grid2 container columnSpacing={2} rowSpacing={1} p={1}>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Dose *"
                  name="dosage"
                  type="text"
                  value={formik.values.dosage}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  margin="normal"
                  slotProps={{ inputLabel: { shrink: true } }}
                  error={formik.touched.dosage && Boolean(formik.errors.dosage)}
                  helperText={formik.touched.dosage && formik.errors.dosage}
                  disabled={deleteMode}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Drug Time *"
                  name="time"
                  type="time"
                  value={selectedDrug?.start_time ? convertUTCToLocalTime(selectedDrug.start_time) : formik.values.time}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  margin="normal"
                  slotProps={{ inputLabel: { shrink: true } }}
                  error={formik.touched.time && Boolean(formik.errors.time)}
                  helperText={formik.touched.time && formik.errors.time}
                  disabled={deleteMode}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="# Days *"
                  name="days"
                  type="number"
                  value={formik.values.days}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  margin="normal"
                  error={formik.touched.days && Boolean(formik.errors.days)}
                  helperText={formik.touched.days && formik.errors.days}
                  disabled={deleteMode}
                />
              </Grid2>
            </Grid2>
            {deleteMode && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems:'center', my: 2 }} pl={1}>
                <WarningRedCircle style={{ height: '48px'}} />
                <Typography fontWeight={700} fontFamily={'nunito'} id="modal-delete-warning-message" variant="h6" component="p" ml={2}>
                    Are you sure you want to delete this medication?
                </Typography>
              </Box>
            )}
            <Box
              className={styles.primary}
              style={{ display: 'flex', justifyContent: 'center' }}
              p={1} 
              mt={2}
            >
              <Button
                fullWidth
                variant="outlined"
                onClick={deleteMode ? () => setDeleteMode(false) : handleDrugModalClose}
                className={styles.modalCancelBtn}
              >
                {deleteMode ? 'Back': 'Cancel'}
              </Button>
              { deleteMode ?
                <Button  fullWidth type='button' variant="contained" className={styles.modalSaveBtn} onClick={handleDelete}>
                  Confirm
                </Button> :
                <Button  fullWidth type="submit" variant="contained" className={styles.modalSaveBtn}>
                  {medId ? 'Update': 'Submit'}
                </Button>
              }
            </Box>
          </form>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddDrugModal;